import axios from "axios";
import { getHeaderToken } from "../utils/serviceHelper";
import { Defect } from "../types/defect";
import { errorToast, successToast } from "@idwal/idwal-react-components";
import { Inspection } from "../types/inspection";
import { AmplifyUser } from "@aws-amplify/ui";
import { saveAs } from "file-saver";

export const getDefects = async (inspectionId: string) => {
  const url =
    process.env.REACT_APP_IDWAL_API_ENDPOINT +
    `/Defects/inspection/${inspectionId}`;

  const headers = await getHeaderToken();

  try {
    const response = await axios.get(url, { headers });

    if (response.data?.defects) {
      return response.data.defects;
    }
  } catch {
    errorToast(`Failed to get defects for inspection: ${inspectionId}`);
  }
};

export const addDefect = async (defect: Defect) => {
  const url =
    process.env.REACT_APP_IDWAL_API_ENDPOINT +
    `/Defects/inspection/${defect.inspectionId}`;

  const headers = await getHeaderToken();

  const data = JSON.stringify(defect);

  try {
    const response = await axios.post(url, data, { headers });

    if (response.data.defect) {
      successToast("Successfully added defect");

      return response.data.defect;
    }
  } catch {
    errorToast("Failed to add defect");
  }
};

export const updateDefect = async (defect: Defect) => {
  const url =
    process.env.REACT_APP_IDWAL_API_ENDPOINT + `/Defects/${defect.defectId}`;

  const headers = await getHeaderToken();

  const data = JSON.stringify(defect);

  try {
    const response = await axios.put(url, data, { headers });

    if (response.status === 200) {
      successToast("Successfully updated defect");

      return true;
    }
  } catch {
    errorToast("Failed to update defect");
  }
};

export const deleteDefect = async (defects: Defect[]) => {
  const url =
    process.env.REACT_APP_IDWAL_API_ENDPOINT +
    `/Defects/inspection/${defects[0].inspectionId}`;

  const token = await getHeaderToken();
  const headers = { Authorization: token.Authorization };

  const ids = defects.map((defect) => defect.defectId).join(",");
  const data = encodeURI(`itemIds=[${ids}]`);

  try {
    const response = await axios.delete(url, { headers, data });

    if (response.status === 200) {
      successToast("Successfully deleted defect(s)");

      return true;
    }
  } catch {
    errorToast("Failed to delete defect");
  }
};

export const generateNotableItems = async (
  inspection: Inspection | undefined,
  user: AmplifyUser
) => {
  if (inspection) {
    const { id: Id, inspectionNumber: InspectionNumber } = inspection;
    const { username, attributes } = user;

    if (Id && InspectionNumber && username && attributes?.email) {
      const url =
        process.env.REACT_APP_NOTABLE_ITEM_BUILDER_API +
        `?inspectionNumber=${encodeURIComponent(
          InspectionNumber
        )}&userName=${encodeURIComponent(username)}` +
        `&userEmail=${encodeURIComponent(
          attributes.email
        )}&inspectionId=${encodeURIComponent(Id)}`;

      const headers = {
        "Content-Type": "application/json",
        "x-api-key": process.env.REACT_APP_NOTABLE_ITEM_BUILDER_KEY,
      };

      try {
        const response = await axios.post(url, undefined, { headers });

        if (response.status === 200) {
          successToast(
            "NIs are being generated soon. You will be notified in Teams when they are ready."
          );
        }
      } catch {
        errorToast(
          "NIs have failed to generate. Please check that a checklist has been submitted."
        );
      }
    }
  }
};

export const getDefectsCodes = async () => {
  const url = process.env.REACT_APP_IDWAL_INTERNAL_API + "/defects/defectCodes";

  const headers = {
    "Content-Type": "application/json",
    "x-api-key": process.env.REACT_APP_IDWAL_INTERNAL_API_KEY,
  };

  try {
    const response = await axios.get(url, { headers });

    if (response.data) {
      return response.data;
    }
  } catch {
    errorToast("Failed to get defect codes");
  }
};

/**
 * Defect extraction will create csv of all defects for inspection, returns a presigned url
 * @param inspectionId
 * @param defects
 */
export const generateActionPlan = async (
  inspectionId: string,
  defects: number[]
) => {
  const url = process.env.REACT_APP_IDWAL_API_ENDPOINT + `/Defects/extract`;

  const headers = await getHeaderToken();

  const data = { data: [{ inspectionId, defects }] };

  try {
    const response = await axios.post(url, data, {
      headers,
      responseType: "blob",
    });

    if (response.data) {
      successToast("Your Defect Action Plan was generated successfully.");
      const contentDisposition = response.headers["content-disposition"];
      const filename = contentDisposition
        ? contentDisposition.split("filename=")[1]
        : "defect-action-plan.csv";

      saveAs(response.data, filename);
    } else {
      errorToast("Your Defect Action Plan failed to generate.");
    }
  } catch {
    errorToast("Your Defect Action Plan failed to generate.");
  }
};
